import CloseIcon from '@mui/icons-material/Close'
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Theme,
} from '@mui/material'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Slide from '@mui/material/Slide'
import { TransitionProps } from '@mui/material/transitions'
import React from 'react'
import { UnitMaster } from '../../../Units/reducer'
import { useCancelTableData } from '../../Cancel/hooks/useTableData'
import { onCancelSubscriptionCallback } from '../hooks/onCancel/onCancel'
import { useAdminUnitDetails } from '../hooks/useUnitDetails'
import { onChangeUnitCallback } from '../hooks/onUnit/onChange'
import { AdminIndividualUnitTable } from './Table'
import { buildUnitOptions } from './utils'

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />
})

interface Props {
  selectedRow: UnitMaster
  onClose: () => void
  individualUnits: UnitMaster[]
  unit: UnitMaster | null
  onUnitChanged: (val: UnitMaster) => void
}

export const AdminIndividualUnitModal: React.FC<Props> = ({
  onClose,
  selectedRow,
  individualUnits,
  unit,
  onUnitChanged,
}) => {
  const cancelTableData = useCancelTableData()
  const { profile, subscriptions, loading, error } =
    useAdminUnitDetails(selectedRow)
  const [isSubmittingChange, setIsSubmittingChange] = React.useState(false)

  const options = buildUnitOptions({
    individualUnits,
    selectedRow,
  })

  const { onChangeUnit } = onChangeUnitCallback({
    selectedRow,
    profile,
    _individualUnits: individualUnits,
    subscriptions,
    onCloseUnitModal: onClose,
    onUnitChanged,
    onChangeStarted: () => setIsSubmittingChange(true),
    onChangeFinished: () => setIsSubmittingChange(false),
  })

  const { onCancelSubscription } = onCancelSubscriptionCallback({
    selectedRow,
    profile,
    subscriptions,
    onCloseUnitModal: onClose,
  })

  const isUnitToBeCancelled = React.useMemo(
    () =>
      !!cancelTableData.find(
        (ctd) => ctd.subscriptionId === String(unit?.subscriptionId)
      ),
    [cancelTableData, unit]
  )

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open
      onClose={(e, reason) => {
        if (reason === 'backdropClick') {
          return
        }
        if (isSubmittingChange) {
          return
        }
        onClose()
      }}
      TransitionComponent={Transition}
    >
      <DialogTitle
        sx={{
          bgcolor: (theme: Theme) =>
            isUnitToBeCancelled
              ? theme.palette.error.dark
              : theme.palette.info.dark,
          py: 1,
          px: 2,
        }}
      >
        {isUnitToBeCancelled ? 'Unit Pending Cancellation' : 'Unit Details'}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => {
          if (isSubmittingChange) {
            return
          }
          onClose()
        }}
        disabled={isSubmittingChange}
        sx={{
          position: 'absolute',
          right: 8,
          top: 5,
          color: (theme) => theme.palette.grey[900],
        }}
      >
        {isSubmittingChange ? (
          <CircularProgress size={18} color="inherit" />
        ) : (
          <CloseIcon />
        )}
      </IconButton>
      <DialogContent
        sx={{
          p: 2,
          pt: '16px !important',
          position: 'relative',
        }}
      >
        {loading ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={2}
            sx={{ minHeight: 180 }}
          >
            <CircularProgress size={28} />
            <Alert severity="info">Loading unit details...</Alert>
          </Stack>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <AdminIndividualUnitTable
            profile={profile}
            options={options}
            onChangeUnit={onChangeUnit}
            unit={unit}
            isUnitToBeCancelled={isUnitToBeCancelled}
            isSubmittingChange={isSubmittingChange}
          />
        )}
        {isSubmittingChange ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (theme: Theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(15, 15, 15, 0.78)'
                  : 'rgba(255, 255, 255, 0.78)',
              zIndex: 1,
            }}
          >
            <Stack alignItems="center" spacing={2}>
              <CircularProgress size={32} />
              <Alert severity="info">Updating unit assignment...</Alert>
            </Stack>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions
        sx={{
          bgcolor: (theme: Theme) => theme.palette.grey[900],
        }}
      >
        <Button
          variant="outlined"
          onClick={onCancelSubscription}
          color="error"
          disabled={
            loading ||
            isSubmittingChange ||
            Boolean(error) ||
            isUnitToBeCancelled ||
            !profile.fullName
          }
        >
          Cancel Subscription
        </Button>
      </DialogActions>
    </Dialog>
  )
}
